import React, { useState, useEffect } from 'react';
import API from '../api';
import { HardHat, ChevronRight, Calendar, Users, Euro } from 'lucide-react';
import CantiereWorkspace from './CantiereWorkspace';
import RowActions from '../components/RowActions';

const statoLabels = { da_avviare: 'Da Avviare', in_corso: 'In Corso', sospeso: 'Sospeso', in_attesa_cliente: 'Attesa Cliente', in_attesa_materiale: 'Attesa Materiale', completato: 'Completato', chiuso: 'Chiuso' };
const statoColors = { da_avviare: 'bg-yellow-100 text-yellow-800', in_corso: 'bg-green-100 text-green-800', sospeso: 'bg-red-100 text-red-800', in_attesa_cliente: 'bg-purple-100 text-purple-700', in_attesa_materiale: 'bg-primary text-primary', completato: 'bg-blue-100 text-blue-800', chiuso: 'bg-slate-200 text-slate-600' };

export default function CantieriPage({ initialCantiereId }) {
  const [cantieri, setCantieri] = useState([]);
  const [selectedId, setSelectedId] = useState(initialCantiereId || null);
  const [eventiUpcoming, setEventiUpcoming] = useState([]);

  const load = () => API.get('/api/cantieri').then(r => setCantieri(r.data));
  useEffect(() => {
    load();
    API.get('/api/cantieri/eventi/upcoming?days=14').then(r => setEventiUpcoming(r.data || [])).catch(() => {});
  }, []);

  if (selectedId) {
    return <CantiereWorkspace cantiereId={selectedId} onBack={() => { setSelectedId(null); load(); }} />;
  }

  return (
    <div className="animate-fade-in" data-testid="cantieri-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <HardHat size={28} className="text-primary" /> Cantieri
          </h1>
          <p className="text-slate-500 mt-1">Gestione completa cantieri: diario, manodopera, materiali, mezzi, calendario, documenti, SAL</p>
        </div>
      </div>

      {/* Eventi prossimi globali */}
      {eventiUpcoming.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4" data-testid="eventi-upcoming">
          <h3 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-1"><Calendar size={14} /> Prossimi eventi (14 giorni)</h3>
          <div className="space-y-1">
            {eventiUpcoming.slice(0, 5).map(e => (
              <button key={e.id} onClick={() => setSelectedId(e.cantiere_id)}
                className="w-full text-left flex items-center gap-2 text-xs py-1 hover:bg-blue-100 rounded px-2">
                <span className="text-[10px] bg-white px-2 py-0.5 rounded font-bold">{e.tipo}</span>
                <span className="font-semibold flex-1 truncate">{e.titolo}</span>
                <span className="text-blue-700 truncate">{e.cantiere_titolo}</span>
                <span className="text-slate-500 flex-shrink-0">{new Date(e.data_inizio).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cantieri.map(c => (
          <div key={c.id} data-testid={`cantiere-card-${c.id}`}
            className="bg-white border border-slate-200 rounded-md p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 group flex flex-col">
            <div className="cursor-pointer flex-1" onClick={() => setSelectedId(c.id)}>
              <div className="flex items-center justify-between mb-3">
                <span className={`px-2 py-1 rounded text-xs font-bold ${statoColors[c.stato] || 'bg-slate-100'}`}>
                  {statoLabels[c.stato] || c.stato}
                </span>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-primary transition-colors" />
              </div>
              <h3 className="font-bold text-lg mb-1">{c.titolo || c.codice_cantiere}</h3>
              <p className="text-xs text-slate-400 font-mono">{c.codice_cantiere}</p>

              {/* Progress bar */}
              <div className="mt-3 mb-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-500">Avanzamento</span>
                  <span className="font-bold text-primary">{c.percentuale_avanzamento || 0}%</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary transition-all" style={{ width: `${c.percentuale_avanzamento || 0}%` }} />
                </div>
              </div>

              {c.cliente_nome && <p className="text-xs text-slate-500 mt-2">{c.cliente_nome}</p>}
              {c.data_apertura && <p className="text-[10px] text-slate-400">Aperto: {new Date(c.data_apertura).toLocaleDateString('it-IT')}</p>}
            </div>
            <div className="flex justify-end mt-3 pt-2 border-t border-slate-100">
              <RowActions entity="cantieri" id={c.id} label="il cantiere" onChange={load} compact />
            </div>
          </div>
        ))}
        {cantieri.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400">
            <HardHat size={48} className="mx-auto mb-3 text-slate-300" />
            <p>Nessun cantiere. Approva un preventivo per aprire il primo cantiere!</p>
          </div>
        )}
      </div>
    </div>
  );
}
